import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Input,
} from '@angular/core';
import { isDefined } from '@sama/js-core';
import { intersection } from 'lodash';
import { filter, map, Observable, switchMap } from 'rxjs';
import { ToolsHelperService } from '../../../../../../cross-team-shared/misc-services/tools-helper.service';
import {
  isUniversalOrVectorTaggingTool,
  ToolName,
  UniversalTool,
  VectorTaggingTool,
} from '../../../../../../cross-team-shared/tools-names';
import { getSelectedToolStore } from '../../../../../h3ml/state/task-workspace-store-utils';
import { arrowIcon } from '../../../../../shared/icons/toolbars/arrow-icon';
import { cuboidIcon } from '../../../../../shared/icons/toolbars/cuboid-icon';
import { editIcon } from '../../../../../shared/icons/toolbars/edit-icon';
import { keypointIcon } from '../../../../../shared/icons/toolbars/keypoint-icon';
import { lineIcon } from '../../../../../shared/icons/toolbars/line-icon';
import { measureIcon } from '../../../../../shared/icons/toolbars/measure-icon';
import { pointIcon } from '../../../../../shared/icons/toolbars/point-icon';
import { polygonIcon } from '../../../../../shared/icons/toolbars/polygon-icon';
import { rectangleIcon } from '../../../../../shared/icons/toolbars/rectangle-icon';
import { sliderRectangleIcon } from '../../../../../shared/icons/toolbars/slider-rectangle-icon';
import {
  AbstractMainToolbarComponent,
  fromSvgIcon,
} from '../abstract-main-toolbar';

export const toolButtonsBaseConfig = {
  Edit: {
    ...fromSvgIcon(editIcon),
    tooltip: 'Select',
  },
  Select: {
    ...fromSvgIcon(editIcon),
    tooltip: 'Select',
  },
  Measure: {
    ...fromSvgIcon(measureIcon),
    tooltip: 'Measure',
  },
  Arrow: {
    ...fromSvgIcon(arrowIcon),
    tooltip: 'Arrow',
  },
  Cuboid: {
    ...fromSvgIcon(cuboidIcon),
    tooltip: '2D Cuboid',
  },
  Keypoint: {
    ...fromSvgIcon(keypointIcon),
    tooltip: 'Keypoint',
  },
  Line: {
    ...fromSvgIcon(lineIcon),
    tooltip: 'Line',
  },
  Point: {
    ...fromSvgIcon(pointIcon),
    tooltip: 'Point',
  },
  Polygon: {
    ...fromSvgIcon(polygonIcon),
    tooltip: 'Polygon',
  },
  Rectangle: {
    ...fromSvgIcon(rectangleIcon),
    tooltip: 'Bounding box',
  },
  SliderRectangle: {
    ...fromSvgIcon(sliderRectangleIcon),
    tooltip: 'Slider rectangle',
  },
};

@Component({
  selector: 'h3-main-video-toolbar',
  templateUrl: './main-video-toolbar.component.html',
  styleUrl: './main-video-toolbar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class MainVideoToolbarComponent extends AbstractMainToolbarComponent<
  UniversalTool | VectorTaggingTool
> {
  @Input() videoOutputName?: string;
  protected override readonly buttonsBaseConfig = toolButtonsBaseConfig;

  private readonly toolsHelperService = inject(ToolsHelperService);

  buttons$ = this.getInput$('videoOutputName').pipe(
    map((outputName: string | undefined) =>
      outputName ? this.buildButtonsConfig(outputName) : [],
    ),
  );
  selectedTool$: Observable<ToolName> = this.getInput$('videoOutputName').pipe(
    filter(isDefined),
    switchMap(
      (outputName) =>
        getSelectedToolStore(this.taskWorkspaceStore, outputName).$,
    ),
    filter(isDefined),
    map((selectedTool) => selectedTool.name),
  );

  protected override getOrderedToolNames() {
    const { readOnly, selectedOutputName } = this.taskWorkspaceStore.state();
    const allowedTools =
      this.toolsHelperService.getAllowedTools(selectedOutputName);
    const orderedTools = [
      'Point',
      'Line',
      'Arrow',
      'Rectangle',
      'SliderRectangle',
      'Polygon',
      'Cuboid',
      'Keypoint',
      'Measure',
      'Zoom',
    ];

    const toolNames = readOnly
      ? ['Select', ...intersection(allowedTools, ['Measure', 'Zoom'])]
      : ['Edit', ...intersection(orderedTools, allowedTools)];
    return toolNames.filter(isUniversalOrVectorTaggingTool);
  }
}
